import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { setStatusRows } from 'src/util/statusSemantics';

// THE DROP-DOWN VALUE API, and the compatibility shim over it (TGCLOUD-1784).
//
// Nineteen files import `getClinicalDefaults`, `saveClinicalDefault` and
// `deleteClinicalDefault` from the clinical forms' own service module, where
// they read and wrote one Mixed blob on the group preferences document. This
// file re-implements those three signatures exactly, against the new routes,
// so every one of those callers keeps working while they are moved over one at
// a time. Without the shim, the cut-over would be a single commit touching
// fourteen pickers, a migration and a Settings screen at once, which is not a
// reviewable change.
//
// SAME SHAPE, SAME SILENCES. The old helpers resolved to an array of
// `{ label }`, returned the list unchanged without writing when the value was
// already there or was blank, and swallowed nothing else. Callers depend on
// all of that: a picker calls the save on EVERY selection, not only on a new
// value, so a shim that posted unconditionally would turn choosing an existing
// option into a network write on every keystroke-completed selection.
//
// WHAT CHANGES BEHIND THE SHIM. Delete is no longer a delete: the X on a
// picker option disables the value for the group, which is what the ticket
// asks for ("removal only prevents the value being selected on future records
// until someone adds it back") and what makes it reversible. The server also
// enforces the role rule for the first time; a genetics user calling the save
// now gets a 403 rather than a silent write.

const LIST_CACHE_HINT = { params: { includeDisabled: false } };

/**
 * Every value a picker should offer for one list, in order.
 *
 * @param {string} listKey
 * @param {{ owner?: string, targetUserGroup?: string }} [options]
 * @returns {Promise<Array<{ _id: string, label: string, meaning: string|null }>>}
 */
export const fetchLookupValues = async (listKey, options = {}) => {
  const params = { ...LIST_CACHE_HINT.params };
  if (options.includeDisabled) params.includeDisabled = true;
  if (options.targetUserGroup) params.targetUserGroup = options.targetUserGroup;
  if (options.scope === 'system') params.scope = 'system';

  const res = await axios.get(APICONSTANTS.lookupList(listKey), { params });
  // `Array.isArray`, NOT `?? []`. If the body is itself an array, `data.values`
  // resolves to `Array.prototype.values`, which is a FUNCTION and therefore not
  // nullish, so the fallback never fires and the caller gets a function where
  // it expected rows. That is not hypothetical: a test double returning
  // `{ data: [] }` for every GET hit exactly this.
  const values = Array.isArray(res?.data?.values) ? res.data.values : [];

  // Teach the status registry here rather than at each caller, so the Overdue
  // panel, the overlays and the pathway board learn a site's own wording from
  // whichever picker happened to load first. Harmless for a list that carries
  // no meanings. A picker's enabled-only read is MERGED rather than replacing
  // what a complete read taught, so a switched-off row keeps its meaning.
  // The deployment's standard set is not what any record is read against.
  if (options.scope !== 'system') {
    setStatusRows(listKey, values, {
      complete: Boolean(options.includeDisabled)
    });
  }
  return values;
};

/**
 * Every list at once, for the Settings screen.
 */
export const fetchAllLookups = async (options = {}) => {
  const params = {};
  if (options.includeDisabled) params.includeDisabled = true;
  if (options.targetUserGroup) params.targetUserGroup = options.targetUserGroup;
  if (options.scope === 'system') params.scope = 'system';

  const res = await axios.get(APICONSTANTS.lookups, { params });
  const body = res?.data;
  const lists = Array.isArray(body?.lists) ? body.lists : [];
  if (options.scope !== 'system') {
    for (const list of lists) {
      setStatusRows(list.listKey, list.values || [], {
        complete: Boolean(options.includeDisabled)
      });
    }
  }
  return {
    lists,
    groups: Array.isArray(body?.groups) ? body.groups : []
  };
};

/**
 * Add a value, or bring back one that was switched off.
 *
 * `owner` is the record ROOT's id, sent only from a clinical record so a
 * System Admin's inline add lands on the group that owns the family rather
 * than in a scratch list nobody reads.
 */
export const createLookupValue = async (listKey, body) => {
  const res = await axios.post(APICONSTANTS.lookupValues(listKey), body);
  return res.data;
};

export const updateLookupValue = async (listKey, id, patch) => {
  const res = await axios.patch(
    APICONSTANTS.lookupValueById(listKey, id),
    patch
  );
  return res.data;
};

export const removeLookupValue = async (listKey, id, params = {}) => {
  const res = await axios.delete(APICONSTANTS.lookupValueById(listKey, id), {
    params
  });
  return res.data;
};

export const reorderLookupValues = async (listKey, orderedIds, body = {}) => {
  const res = await axios.post(APICONSTANTS.lookupReorder(listKey), {
    orderedIds,
    ...body
  });
  return res.data;
};

/**
 * Publish a value as a deployment standard and give it to every group.
 * System Admin only; the server refuses anyone else.
 */
export const publishStandardValue = async (listKey, body) => {
  const res = await axios.post(APICONSTANTS.lookupStandards(listKey), body);
  return res.data;
};

/** Withdraw one. A retire, never a delete: records still hold the value. */
export const retireStandardValue = async (listKey, id) => {
  const res = await axios.patch(APICONSTANTS.lookupStandardById(listKey, id), {
    retired: true
  });
  return res.data;
};

/**
 * Count how many records hold each value, and find the values on records that
 * the list does not contain.
 *
 * Returns `{ supported, counted, updated, unlisted: [{label, count}] }`. A
 * list stored somewhere other than a clinical note answers `supported: false`
 * rather than a zero that would read as "nothing uses these".
 */
export const scanLookupUsage = async (listKey, options = {}) => {
  const body = {};
  if (options.targetUserGroup) body.targetUserGroup = options.targetUserGroup;
  const res = await axios.post(APICONSTANTS.lookupScan(listKey), body);
  return res.data;
};

/** Add values the scan found on records to the list. */
export const adoptLookupValues = async (listKey, labels, body = {}) => {
  const res = await axios.post(APICONSTANTS.lookupAdopt(listKey), {
    labels,
    ...body
  });
  return res.data;
};

/**
 * Every list for this scope, in one file.
 *
 * The workflow: a client sets their lookups up on a pre-production instance and
 * the whole set is carried to production at go-live.
 */
export const exportLookupBundle = async (options = {}) => {
  const params = {};
  if (options.targetUserGroup) params.targetUserGroup = options.targetUserGroup;
  if (options.scope === 'system') params.scope = 'system';
  const res = await axios.get(APICONSTANTS.lookupBundleExport, { params });
  return res.data;
};

/**
 * Apply an exported bundle to this scope.
 *
 * `dryRun` previews and writes nothing — the screen always previews first,
 * because this writes a whole group's vocabulary in one go. The import never
 * deletes: values the target has and the bundle does not come back as
 * `extras`, untouched.
 *
 * @param {object} bundle the parsed export file
 * @param {{ mode?: 'mirror'|'additive', dryRun?: boolean, targetUserGroup?: string }} [options]
 */
export const importLookupBundle = async (bundle, options = {}) => {
  const body = { bundle, mode: options.mode || 'mirror' };
  if (options.dryRun) body.dryRun = true;
  if (options.targetUserGroup) body.targetUserGroup = options.targetUserGroup;
  const res = await axios.post(APICONSTANTS.lookupBundleImport, body);
  return res.data;
};

/** The list in the shape a delivery migration takes. */
export const exportLookupList = async (listKey, options = {}) => {
  const params = {};
  if (options.targetUserGroup) params.targetUserGroup = options.targetUserGroup;
  if (options.scope === 'system') params.scope = 'system';
  const res = await axios.get(APICONSTANTS.lookupExport(listKey), { params });
  return res.data;
};

// --------------------------------------------------------------- the shim
//
// Below this line the three original signatures, unchanged, so the existing
// callers compile and behave as they did.

// Case- and whitespace-insensitive, the same rule the old helper used and the
// same one the server's unique index enforces. Two spellings of one value in a
// drop-down is a reporting defect, not just untidy.
const sameLabel = (a, b) =>
  String(a ?? '')
    .trim()
    .toLowerCase() ===
  String(b ?? '')
    .trim()
    .toLowerCase();

/**
 * @param {string} typeKey
 * @returns {Promise<Array<{ label: string }>>}
 */
export const getClinicalDefaults = async (typeKey) => {
  const values = await fetchLookupValues(typeKey);
  // The old shape, exactly: callers read `option.label` and nothing else. The
  // row id rides along for the callers that have moved on to needing it.
  return values.map((value) => ({
    label: value.label,
    _id: value._id,
    // Carried through because the status pickers need them; harmless
    // elsewhere. `isDefault` is what a NEW appointment starts on (TGCLOUD-1751).
    meaning: value.meaning ?? null,
    isDefault: Boolean(value.isDefault)
  }));
};

/**
 * @param {string} typeKey
 * @param {string} label
 * @returns {Promise<Array<{ label: string }>>} the list as it now stands
 */
export const saveClinicalDefault = async (typeKey, label) => {
  const current = await getClinicalDefaults(typeKey);

  // NOT A NETWORK WRITE when there is nothing to write. A picker calls this on
  // every selection, so posting unconditionally would write on every choice of
  // an existing option. The old helper returned early here and its tests
  // assert that no request was made.
  const trimmed = String(label ?? '').trim();
  if (!trimmed) return current;
  if (current.some((opt) => sameLabel(opt.label, trimmed))) return current;

  await createLookupValue(typeKey, { label: trimmed });
  return getClinicalDefaults(typeKey);
};

/**
 * Stop offering a value.
 *
 * The old helper removed it from the array. This disables the row instead, so
 * the value keeps its position, its meaning and its provenance for when
 * someone adds it back, and every record still holding it is untouched.
 *
 * @param {string} typeKey
 * @param {string} label
 * @returns {Promise<Array<{ label: string }>>} the list as it now stands
 */
export const deleteClinicalDefault = async (typeKey, label) => {
  const current = await getClinicalDefaults(typeKey);
  const target = current.find((opt) => sameLabel(opt.label, label));
  if (!target || !target._id) return current;

  await updateLookupValue(typeKey, target._id, { enabled: false });
  return getClinicalDefaults(typeKey);
};
